import * as React from 'react';

import { CheckIcon } from 'lucide-react'

import { useTheme, styled, alpha, Box, Typography, Stack, Step, StepLabel, Stepper as MuiStepper } from '@mui/material';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.brand[500],
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.brand[500],
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const QontoStepIconRoot = styled('div')<{ ownerState: { active?: boolean } }>(
  ({ theme, ownerState }) => ({
    color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#eaeaf0',
    display: 'flex',
    height: 22,
    alignItems: 'center',
    ...(ownerState.active && {
      color: theme.palette.brand[500],
    }),
    '& .QontoStepIcon-completedIcon': {
      color: theme.palette.brand[500],
      zIndex: 1,
      fontSize: 18,
    },
    '& .QontoStepIcon-circle': {
      width: 24,
      height: 24,
      borderRadius: '50%',
      backgroundColor: 'currentColor',
    },
  }),
);

function QontoStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;
  const theme = useTheme();
  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <CheckIcon color={theme.palette.brand[500]} />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}


interface StepperProps {
  steps: string[],
  activeStep: number,
  orientation?: 'horizontal' | 'vertical',
  /**
   * `dots` (default) = the Qonto dot-and-connector stepper.
   * `bar` = a segmented progress bar (one segment per step) with the current
   * step accented in brand, completed steps neutral, and future steps muted —
   * lighter chrome for short flows. The active step's label is shown beneath.
   */
  variant?: 'dots' | 'bar',
}

export const Stepper = ({ steps , activeStep = 1, variant = 'dots' } : StepperProps) => {
  if (variant === 'bar') {
    return (
      <Stack sx={{ width: '100%' }} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" aria-label="Progress">
          {steps.map((label, i) => (
            <Box
              key={label}
              aria-current={i === activeStep ? 'step' : undefined}
              sx={(theme) => ({
                flex: 1,
                height: 4,
                borderRadius: 999,
                transition: 'background-color .2s ease, box-shadow .2s ease',
                // Completed steps are BRAND, not near-black. They used to be
                // alpha(text.primary, 0.7), which on a light surface renders an almost
                // black bar sitting next to a purple one — the finished work reads as
                // the heaviest thing in the control, and the two bars look like
                // different kinds of object rather than one progress track. The dots
                // variant in this same file already paints active and completed alike
                // (brand[500]); the bar variant was the outlier.
                //
                // Completed is dimmed to 0.45 rather than made identical, so the track
                // still says where you are: full brand = here, dim brand = done, grey
                // = ahead. The dark-mode glow below reinforces the current step.
                backgroundColor:
                  i === activeStep
                    ? theme.palette.brand[500]
                    : i < activeStep
                      ? alpha(theme.palette.brand[500], 0.45)
                      : theme.palette.primary[100],
                // Soft brand glow on the current step so it reads as "you are here".
                // Dark mode only — on a light surface the glow reads muddy/off.
                ...(i === activeStep && theme.palette.mode === 'dark' && {
                  boxShadow: `0 0 8px ${alpha(theme.palette.brand[500], 0.6)}`,
                }),
              })}
            />
          ))}
        </Stack>
        {steps[activeStep] && (
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 13 }}>
            Step {activeStep + 1} of {steps.length} · {steps[activeStep]}
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <Stack sx={{ width: '100%' }} spacing={4}>
      <MuiStepper alternativeLabel activeStep={activeStep} connector={<QontoConnector />}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel StepIconComponent={QontoStepIcon}>{label}</StepLabel>
          </Step>
        ))}
      </MuiStepper>
    </Stack>
  );
}





const ColorlibConnector = styled(StepConnector)(({ theme, step, length}) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 18,
  },
  // Only COMPLETED legs are purple — the active (current, in-progress) leg stays
  // muted so the line reads "purple up to the last reached milestone, then pending".
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        'linear-gradient(90deg, rgba(130,90,195,1) 0%, rgb(55, 39, 137) 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    // Not-yet-completed segments: a muted zinc line (grey[800] resolved to a bright
    // near-white in dark mode).
    backgroundColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#eaeaf0',
    borderRadius: 1,
  },
  // TODO: this is where the error lies on coloring (See tags order page)
  minWidth: '100%',
  left: 'calc(-100% + 36px)',
  right: 'calc(0% + 36px)'
}));

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean, step?: number, length?: number };
}>(({ theme }) => ({
  zIndex: 1,
  // Not-yet-completed steps (current + future): a muted zinc circle — dark zinc
  // fill, zinc border, grey icon — so an un-reached phase reads as "pending", not
  // as a bright/filled milestone. Only COMPLETED steps get the purple fill below.
  color: theme.palette.mode === 'dark' ? '#a1a1aa' : '#71717a',
  width: 36,
  height: 36,
  display: 'flex',
  borderRadius: '50%',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#3f3f46' : theme.palette.grey[300]}`,
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? '#27272a' : theme.palette.white[100],
  variants: [
    // Completed steps only — the purple gradient marks a reached milestone. The
    // active (current, in-progress) step keeps the pending zinc style above.
    {
      props: ({ ownerState }) => ownerState.completed,
      style: {
        color: 'white',
        backgroundImage:
        'linear-gradient(90deg, rgba(130,90,195,1) 0%, rgb(55, 39, 137) 100%)',
      },
    },
  ],
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className, step, length, icon } = props;

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} step={step} length={length} className={className}>
      {icon}
    </ColorlibStepIconRoot>
  );
}

interface ShippingStepperProps {
  steps: Step[],
  activeStep: number,
  orientation?: 'horizontal' | 'vertical',
}

interface Step {
  label: string,
  icon: React.ReactNode
}

export const ShippingStepper = ({ steps , activeStep = 1, orientation = 'horizontal'} : ShippingStepperProps) => {

  return (
    <MuiStepper alternativeLabel orientation={orientation} activeStep={activeStep} connector={<ColorlibConnector />}>
      {steps.map((step, index) => (
        <Step 
          key={step.label} 
          sx={{ padding: 0,}}>
          <StepLabel 
          sx={{
            alignItems: index == 0 ? 'flex-start' : index == steps.length - 1 ? 'flex-end' : 'flex',
          }}
          slotProps={{
            label: {
              sx: {
                textAlign: index == 0 ? 'left' : index == steps.length - 1 ? 'right' : 'center',
              }
            }
          }}
          StepIconComponent={() => ColorlibStepIcon({active: index == activeStep, completed: index < activeStep, className: '', step: index, length: steps.length, icon: step.icon})}
          >
            {step.label}
          </StepLabel>
        </Step>
      ))}
    </MuiStepper>
  );
}